/**
 * Installed Languages - SINGLE SOURCE OF TRUTH
 *
 * This file defines which languages are INSTALLED in the application.
 * Installed languages are determined by what translation files exist on disk.
 *
 * IMPORTANT: This list must be kept in sync with the folders in
 * `src/shared/config/locales/`, which is the ONLY source of translations —
 * they are imported into the JS bundle by `shared/lib/i18n.ts`.
 *
 * There used to be a second copy under `web/public/locales/` that was fetched
 * at runtime and merged over the bundled one. The two silently diverged, so
 * the public copy was removed. Do not reintroduce it: a catalog that is both
 * bundled and fetched has no single source of truth.
 *
 * When adding a new language:
 * 1. Create src/shared/config/locales/{lang-code}/common.json
 * 2. Import it into STATIC_RESOURCES in shared/lib/i18n.ts
 * 3. Add the language code to INSTALLED_LANGUAGES below
 *
 * The distinction between installed and available:
 * - INSTALLED: Languages that have translation files on disk (fixed, requires deployment to change)
 * - AVAILABLE: Languages exposed to end users in settings (configurable, subset of installed)
 */

/**
 * List of all installed language codes.
 * These are the languages that have translation files in
 * src/shared/config/locales/ and are bundled by shared/lib/i18n.ts.
 */
export const INSTALLED_LANGUAGES = ["ar", "en"] as const;

/**
 * Type for installed language codes
 */
export type InstalledLanguage = (typeof INSTALLED_LANGUAGES)[number];

/**
 * Human-readable labels for installed languages
 */
export const INSTALLED_LANGUAGE_LABELS: Record<InstalledLanguage, string> = {
	ar: "العربية",
	en: "English",
};

/**
 * Check if a language code is installed
 */
export const isInstalledLanguage = (lang: string): lang is InstalledLanguage =>
	(INSTALLED_LANGUAGES as readonly string[]).includes(lang);

/**
 * Filter a list of languages to only include installed ones
 */
export const filterToInstalledLanguages = (
	languages: string[],
): InstalledLanguage[] => languages.filter(isInstalledLanguage);

/**
 * Ensure a list of languages only contains installed languages,
 * and always includes the default language (en)
 */
export const normalizeAvailableLanguages = (
	languages: string[],
): InstalledLanguage[] => {
	const filtered = filterToInstalledLanguages(languages);
	// Always ensure English is included
	if (!filtered.includes("en")) {
		filtered.push("en");
	}
	return filtered.sort();
};
